/**
 * Gift-with-purchase reconciliation.
 *
 * product-form.js adds the gift line automatically when its "parent"
 * product is added to the cart (tagged via line item properties
 * `_gift: 'true'` and `_gift_of: <parent variant id>`). This script is the
 * self-healing other half, fixing two situations no matter how they
 * happened — a missed check in product-form.js, two add-to-cart entry
 * points racing each other, or the cart being edited directly via the
 * Shopify cart API outside the theme entirely:
 *
 * 1. Orphaned gifts — the parent product was removed (or its quantity
 *    dropped to zero), so the gift line is no longer justified.
 * 2. Duplicate gifts — more than one gift line exists for the same parent
 *    product (e.g. from a race between two near-simultaneous submissions);
 *    only the first one found is kept.
 *
 * Runs on initial load (in case the cart was edited elsewhere, e.g. a
 * previous session or another device) and on every `on:line-item:change`
 * event, which the theme already dispatches from both the cart page
 * (cart-items.js) and the cart drawer (which reuses the same component).
 */
(() => {
  let syncing = false;

  async function syncGiftsWithPurchase() {
    if (syncing) return;
    syncing = true;

    try {
      const cartResponse = await fetch(`${theme.routes.cart}.js`);
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();

      const presentVariantIds = new Set(
        cart.items.map((item) => String(item.variant_id))
      );

      const seenGiftOf = new Set();
      const linesToRemove = [];

      cart.items.forEach((item) => {
        if (!item.properties || item.properties._gift !== 'true' || !item.properties._gift_of) return;

        const giftOf = String(item.properties._gift_of);
        const isOrphaned = !presentVariantIds.has(giftOf);
        const isDuplicate = seenGiftOf.has(giftOf);

        if (isOrphaned || isDuplicate) {
          linesToRemove.push(item);
        } else {
          seenGiftOf.add(giftOf);
        }
      });

      if (linesToRemove.length === 0) return;

      for (const gift of linesToRemove) {
        // eslint-disable-next-line no-await-in-loop
        await fetch(theme.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: gift.key, quantity: 0 })
        });
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.refresh();
      } else if (window.location.pathname === theme.routes.cart) {
        window.location.reload();
      }
    } finally {
      syncing = false;
    }
  }

  document.addEventListener('on:line-item:change', syncGiftsWithPurchase);
  document.addEventListener('DOMContentLoaded', syncGiftsWithPurchase, { once: true });
})();
